import moment from "moment";
import { Providers } from "@tripian/model";

/**
 * Single source of the cancellation-policy wording shown on screen and printed on the
 * downloadable ticket. The two must never diverge — the PDF is the customer's record of
 * the terms they accepted.
 *
 * Livn sends either free text or a rule ladder where each rule states the fee that
 * applies from `after` onwards; the ladder is flattened into readable intervals.
 */
export const livnCancellationPolicyLines = (policy?: Providers.Livn.CancellationPolicy): string[] => {
  if (!policy) return [];
  if (policy.rules?.length) {
    const rules = [...policy.rules].sort((a, b) => new Date(a.after).getTime() - new Date(b.after).getTime());
    return rules.map((rule, i) => {
      const next = rules[i + 1];
      const label = next
        ? `Until ${moment(next.after).format("MMM D, YYYY")}`
        : `From ${moment(rule.after).format("MMM D, YYYY")}`;
      const cost = !rule.cancellationPossible
        ? "cannot be cancelled"
        : rule.cancellationCostPerc === 0
        ? "free cancellation"
        : `${rule.cancellationCostPerc}% cancellation fee`;
      return `${label}: ${cost}`;
    });
  }
  return policy.text ? [policy.text] : [];
};

/** The policy lives on the booking, or failing that on the first ticket's booking details. */
export const livnBookingCancellationPolicy = (
  booking: Providers.Livn.Booking
): Providers.Livn.CancellationPolicy | undefined =>
  booking.cancellationPolicy ?? booking.tickets?.[0]?.bookingDetails?.cancellationPolicy;
